import { CommonModule } from '@angular/common';
import { Component, computed, effect, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RadioButtonModule } from 'primeng/radiobutton';
import { finalize } from 'rxjs';
import { AnswerType } from '@/shared/enums/generic.model';
import { CategoryQuestion } from '@/shared/models/Category.model';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { CheckboxComponent } from '@/shared/ui-components/checkbox/checkbox.component';
import { DialogComponent } from '@/shared/ui-components/dialog/dialog.component';
import { InputComponent } from '@/shared/ui-components/input/input.component';
import { HelpersService } from '@/shared/services/helpers.service';
import { GlobalProgressSpinnerService } from '@/shared/services/global-progress-spinner.service';
import { ComplianceRequestsService } from '@/core/compliance/compliance-requests.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import {
    buildFullDealPreferencesPayload,
    buildPreferencePayloadForQuestion,
    DEAL_PREFERENCE_QUESTION_DEPENDENCIES,
    DealPreferenceQuestionFormValue,
    getChildQuestionDependency,
    isParentTriggerSelectedInForm,
    UnansweredQuestionRef,
} from '@/pages/user-profile/data-access/deal-preferences.utils';

@Component({
    selector: 'app-deal-preferences-prompt-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        DialogComponent,
        ButtonComponent,
        CheckboxComponent,
        InputComponent,
        RadioButtonModule,
    ],
    templateUrl: './deal-preferences-prompt-dialog.component.html',
})
export class DealPreferencesPromptDialogComponent {
    private readonly _fb = inject(FormBuilder);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _userProfileService = inject(UserProfileService);
    private readonly _complianceRequestsService = inject(ComplianceRequestsService);
    private readonly _globalProgressSpinnerService = inject(GlobalProgressSpinnerService);
    private readonly _helpersService = inject(HelpersService);
    private readonly _translateService = inject(TranslateService);

    public readonly showDialog = computed(
        () =>
            this._userProfileService.showDealPreferencesPrompt() &&
            !this._complianceRequestsService.hasPendingRequests()
    );
    public readonly promptQuestion = this._userProfileService.promptQuestion;
    public readonly saving = signal(false);
    public readonly AnswerType = AnswerType;
    public readonly showCategoryHeader = signal(false);

    public questionForm: FormGroup | null = null;
    public activeRef: UnansweredQuestionRef | null = null;

    constructor() {
        effect(() => {
            const ref = this.promptQuestion();
            if (!ref || !this._userProfileService.showDealPreferencesPrompt()) {
                this.questionForm = null;
                this.activeRef = null;
                return;
            }

            this.activeRef = ref;
            this.showCategoryHeader.set(ref.displayQuestionIndices.length > 1);
            this.questionForm = this._fb.group({
                questions: this._fb.array(
                    ref.displayQuestionIndices.map((questionIndex) =>
                        this._fb.group({
                            questionId: [ref.category.questions[questionIndex].id],
                            answers: this.createAnswersArray(ref.category.questions[questionIndex]),
                        })
                    )
                ),
            });

            this.setupChildQuestionDependency(ref);
        });
    }

    public get questionsArray(): FormArray {
        return this.questionForm?.get('questions') as FormArray;
    }

    public getQuestionAt(formIndex: number): CategoryQuestion | null {
        const ref = this.activeRef;
        if (!ref) return null;
        const questionIndex = ref.displayQuestionIndices[formIndex];
        return ref.category.questions[questionIndex] ?? null;
    }

    public getQuestionAnswers(formIndex: number): FormArray {
        return this.questionsArray.at(formIndex).get('answers') as FormArray;
    }

    public isQuestionVisible(formIndex: number): boolean {
        const ref = this.activeRef;
        const question = this.getQuestionAt(formIndex);
        if (!ref || !question) return false;

        const dependency = getChildQuestionDependency(ref.categoryIndex, ref.displayQuestionIndices[formIndex]);
        if (!dependency) return true;

        const parentFormIndex = ref.displayQuestionIndices.indexOf(dependency.parentQuestionIndex);
        if (parentFormIndex < 0) return false;

        const parentForm = this.questionsArray.at(parentFormIndex).getRawValue() as DealPreferenceQuestionFormValue;
        return isParentTriggerSelectedInForm(parentForm, dependency.triggerAnswerId);
    }

    public onSkip(): void {
        this._userProfileService.dismissDealPreferencesPrompt();
    }

    public onDialogVisibleChange(visible: boolean): void {
        if (!visible) {
            this.onSkip();
        }
    }

    public onSave(): void {
        const ref = this.activeRef;
        if (!ref || !this.questionForm || this.questionForm.invalid) {
            this.questionForm?.markAllAsTouched();
            return;
        }

        const formQuestions = this.questionsArray.getRawValue() as DealPreferenceQuestionFormValue[];
        const primaryFormIndex = ref.displayQuestionIndices.indexOf(ref.questionIndex);
        const primaryQuestion = ref.category.questions[ref.questionIndex];
        const primaryPayload = buildPreferencePayloadForQuestion(
            ref.category,
            primaryQuestion,
            formQuestions[primaryFormIndex]
        );

        if (!primaryPayload) {
            this._helpersService.showErrorMessage(
                this._translateService.instant('USER_PROFILE.DEAL_PREFERENCES_PROMPT.ANSWER_REQUIRED') as string
            );
            return;
        }

        const dialogUpdates = ref.displayQuestionIndices
            .map((questionIndex, formIndex) => ({
                categoryIndex: ref.categoryIndex,
                questionIndex,
                formQuestion: formQuestions[formIndex],
            }))
            .filter((_, formIndex) => this.isQuestionVisible(formIndex));

        const preferences = buildFullDealPreferencesPayload(
            this._userProfileService.categoryPreferences(),
            dialogUpdates
        );

        this.saving.set(true);
        this._globalProgressSpinnerService.showLoader();
        this._userProfileService
            .saveDealPreferences(preferences)
            .pipe(
                finalize(() => {
                    this.saving.set(false);
                    this._globalProgressSpinnerService.hideLoader();
                })
            )
            .subscribe({
                next: () => {
                    this._userProfileService.fetchCategoryPreferencesForReload().subscribe({
                        next: () => {
                            this._userProfileService.dismissDealPreferencesPromptAfterSave();
                            this._helpersService.showSuccessMessage(
                                this._translateService.instant('USER_PROFILE.DEAL_PREFERENCES_PROMPT.SAVE_SUCCESS') as string
                            );
                        },
                    });
                },
                error: (err) => this._helpersService.showErrorMessage(err),
            });
    }

    public onRadioSelect(formIndex: number, answerIndex: number): void {
        const answersArray = this.getQuestionAnswers(formIndex);
        for (let i = 0; i < answersArray.length; i++) {
            answersArray.at(i).get('selected')?.setValue(i === answerIndex);
        }
        this.syncChildQuestionState();
    }

    public onTextInput(event: Event, formIndex: number, answerIndex: number): void {
        const input = event.target as HTMLInputElement;
        const control = this.getQuestionAnswers(formIndex)?.at(answerIndex).get('answerText');
        if (!control) return;
        if (input.value.length > 200) {
            input.value = input.value.substring(0, 200);
            control.setValue(input.value);
        }
        control.markAsTouched();
    }

    public isOtherAnswer(answerLabel: string): boolean {
        return answerLabel.toLowerCase().startsWith('other');
    }

    private setupChildQuestionDependency(ref: UnansweredQuestionRef): void {
        const dependency = DEAL_PREFERENCE_QUESTION_DEPENDENCIES.find((d) => d.categoryIndex === ref.categoryIndex);
        if (!dependency) return;

        const parentFormIndex = ref.displayQuestionIndices.indexOf(dependency.parentQuestionIndex);
        if (parentFormIndex < 0) return;

        const parentAnswers = this.getQuestionAnswers(parentFormIndex);
        this.applyChildQuestionState(ref, parentAnswers.getRawValue());

        parentAnswers.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((answers) => {
            this.applyChildQuestionState(ref, answers);
        });
    }

    private syncChildQuestionState(): void {
        const ref = this.activeRef;
        if (!ref) return;

        const dependency = getChildQuestionDependency(ref.categoryIndex, ref.displayQuestionIndices[1] ?? -1);
        if (!dependency) return;

        const parentFormIndex = ref.displayQuestionIndices.indexOf(dependency.parentQuestionIndex);
        if (parentFormIndex < 0) return;

        this.applyChildQuestionState(ref, this.getQuestionAnswers(parentFormIndex).getRawValue());
    }

    private applyChildQuestionState(
        ref: UnansweredQuestionRef,
        parentAnswers: { selected: boolean; answerId: number }[]
    ): void {
        const dependency = DEAL_PREFERENCE_QUESTION_DEPENDENCIES.find((d) => d.categoryIndex === ref.categoryIndex);
        if (!dependency) return;

        const childFormIndex = ref.displayQuestionIndices.indexOf(dependency.childQuestionIndex);
        if (childFormIndex < 0) return;

        const triggerMet = parentAnswers.some((a) => a.selected && a.answerId === dependency.triggerAnswerId);
        const childAnswers = this.getQuestionAnswers(childFormIndex);

        childAnswers.controls.forEach((ctrl) => {
            const selectedCtrl = ctrl.get('selected');
            if (!selectedCtrl) return;

            if (triggerMet) {
                selectedCtrl.enable({ emitEvent: false });
            } else {
                selectedCtrl.disable({ emitEvent: false });
                selectedCtrl.setValue(false, { emitEvent: false });
                ctrl.get('answerText')?.setValue('', { emitEvent: false });
            }
        });
    }

    private createAnswersArray(question: CategoryQuestion): FormArray {
        const selectedAnswers = question.clientPreference?.selections || [];
        const answers =
            question.answer.length > 0
                ? question.answer
                : [{ id: 0, categoryId: question.categoryId, categoryQuestionId: question.id, answer: '' }];

        return this._fb.array(
            answers.map((answer) => {
                const selection = selectedAnswers.find((s) => s.categoryQuestionAnswerId === answer.id);
                return this._fb.group({
                    answerId: [answer.id],
                    selected: [!!selection],
                    answerText: [selection?.answerText || '', [Validators.maxLength(200)]],
                });
            })
        );
    }
}
